import { useEffect } from 'react';
import { Timezone, withCurrentTimezone } from '../../state/Timezone';
import { useRecoilState } from 'recoil';
import { DateTime } from 'luxon';
import { useLocales } from './useLocales';
import { EASTERN_TIME_ZONE, PACIFIC_TIME_ZONE, getZoneOffsetHours, toGmtLabel } from '../../utils/dateHelpers';

type ReturnType = {
  onChangeTimezone: (newTz: Timezone) => void;
  currentTimezone: Timezone;
  getTimezonesList: () => Timezone[];
  timestamp: (date: string, format?: string) => string;
};

let isInitialized = false;
const localUtcOffset = DateTime.now().offset / 60;
const utcString = `LOCAL (${toGmtLabel(localUtcOffset)})`;

/** Labels persisted before ET/PT replaced the season-specific pairs. */
const legacyLabels: Record<string, string> = {
  EST: 'ET',
  EDT: 'ET',
  PST: 'PT',
  PDT: 'PT'
};

/**
 * The US entries carry an IANA zone rather than a season-specific label, because which of EST/EDT
 * applies depends on the date being rendered, not on whether the editor's own zone is currently in
 * daylight time. `offset` stays for the dropdown description only.
 */
export const getTimezonesList = (): Timezone[] => {
  const easternOffset = getZoneOffsetHours(EASTERN_TIME_ZONE);
  const pacificOffset = getZoneOffsetHours(PACIFIC_TIME_ZONE);

  return [
    { label: 'Local', offset: localUtcOffset, description: utcString, isLocal: true },
    { label: 'GMT', offset: 0, zone: 'UTC', description: 'GMT' },
    {
      label: 'ET',
      offset: easternOffset,
      zone: EASTERN_TIME_ZONE,
      description: `ET (${toGmtLabel(easternOffset)})`
    },
    {
      label: 'PT',
      offset: pacificOffset,
      zone: PACIFIC_TIME_ZONE,
      description: `PT (${toGmtLabel(pacificOffset)})`
    }
  ];
};

export function useTimezones(): ReturnType {
  const [currentTimezone, setCurrentTimezone] = useRecoilState(withCurrentTimezone);
  const { currentLang } = useLocales();

  const handleChangeTimezoneLabel = (newTimezone: Timezone) => {
    setCurrentTimezone(newTimezone);
  };

  const timestamp = (date: string, format = 'EEE, ff') => {
    const timezone = getCurrentTimezone();
    const parsed = DateTime.fromISO(date).setLocale(currentLang);
    // Resolving through the zone keeps the offset tied to `date` rather than to today, so
    // timestamps either side of a DST boundary render with the offset that was actually in effect.
    if (timezone.zone) return parsed.setZone(timezone.zone).toFormat(format);
    return parsed.plus({ hours: timezone.offset - localUtcOffset }).toFormat(format);
  };

  const getCurrentTimezone = (): Timezone => {
    const currentTimezoneList = getTimezonesList();
    const label = legacyLabels[currentTimezone.label] ?? currentTimezone.label;
    return currentTimezoneList.find((tz) => tz.label === label) || currentTimezoneList[0];
  };

  useEffect(() => {
    if (!isInitialized) {
      setCurrentTimezone(getCurrentTimezone());
      isInitialized = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTimezone]);

  return {
    onChangeTimezone: handleChangeTimezoneLabel,
    currentTimezone: getCurrentTimezone(),
    getTimezonesList,
    timestamp
  };
}
